import { logoutUser, getLoggedInUser, getDeviceKey, CSRF_TOKEN_HEADER_KEY, getCsrfToken, updateAccessTokenExpirationTime, updateCsrfToken, isAccessTokenExpired } from 'services/AuthService';
import axios from 'axios';
import config from 'config';

const url = (uri) => `${config.apiRoot}${uri}`;

function defaultAuthHeaders() {
  return { [CSRF_TOKEN_HEADER_KEY]: getCsrfToken() };
}

function isCanceledAxiosError(error) {
  return (
    error &&
    (error.code === 'ERR_CANCELED' ||
      error.name === 'CanceledError' ||
      (typeof axios.isCancel === 'function' && axios.isCancel(error)))
  );
}

const parseRequestTimeout = (timeout) => {
  if (timeout === null) return 0;
  const n = Number(timeout);
  return Number.isFinite(n) ? n : 0;
}

export function sendAsyncRequest(uri, method, data, onSuccessCallback, onFailureCallback){
  axios({
    method: method,
    url: url(uri),
    data: data,
    withCredentials: true
  }).then(function (response) {
    onSuccessCallback(response);
  }).catch(function (error) {
    onFailureCallback(error);
  });
}

function asyncAuthRequestWithUrlResolver(uri, method, data, onSuccessCallback, onFailureCallback, responseType, onFinallyCallback, signal, timeout, resolveUrl, buildHeaders) {
  const resolveRequestUrl = resolveUrl || url;
  const buildRequestHeaders = buildHeaders || defaultAuthHeaders;

  if (isAccessTokenExpired()) {
    refreshToken(uri, method, data, onSuccessCallback, onFailureCallback, responseType, onFinallyCallback, signal, timeout, resolveRequestUrl, buildRequestHeaders);
  } else {
    const axiosConfig = {
      method: method,
      url: resolveRequestUrl(uri),
      data: data,
      responseType: responseType,
      headers: buildRequestHeaders(),
      withCredentials: true,
      signal: signal,
      timeout: parseRequestTimeout(timeout),
    };

    axios(axiosConfig)
      .then(function (response) {
        onSuccessCallback(response);
      })
      .catch(function (error) {
        if (isCanceledAxiosError(error)) {
          return;
        }
        if (error.response && error.response.status === 401) {
          refreshToken(uri, method, data, onSuccessCallback, onFailureCallback, responseType, onFinallyCallback, signal, timeout, resolveRequestUrl, buildRequestHeaders);
        } else {
          if (typeof onFailureCallback === 'function')
            onFailureCallback(error.response || error);
          else
            console.log(error.response);
        }
      })
      .finally(() => {
        if (signal?.aborted) return;
        if (typeof onFinallyCallback === 'function') onFinallyCallback();
      });
  }
}

function asyncAuthRequest(uri, method, data, onSuccessCallback, onFailureCallback, responseType, onFinallyCallback, signal, timeout){
  asyncAuthRequestWithUrlResolver(uri, method, data, onSuccessCallback, onFailureCallback, responseType, onFinallyCallback, signal, timeout, url, defaultAuthHeaders);
}

export function sendAuthenticatedAsyncRequest(uri, method, data, onSuccessCallback, onFailureCallback){
  asyncAuthRequest(uri, method, data, onSuccessCallback, onFailureCallback, null, null, null, null);
}

export function sendAuthenticatedAsyncRequestWithResponseType(uri, method, data, onSuccessCallback, onFailureCallback, responseType){
  asyncAuthRequest(uri, method, data, onSuccessCallback, onFailureCallback, responseType, null, null, null);
}

export function sendAuthenticatedAsyncRequestWithFinallyCallback(uri, method, data, onSuccessCallback, onFailureCallback, onFinallyCallback, signal, timeout){
  asyncAuthRequest(uri, method, data, onSuccessCallback, onFailureCallback, null, onFinallyCallback, signal, timeout);
}

function customersApiServiceUrl(uri) {
	return `${config.customersApiServiceRoot}${uri.startsWith('/') ? uri : `/${uri}`}`;
}

function customersApiAuthHeaders() {
  return { [CSRF_TOKEN_HEADER_KEY]: getCsrfToken(), 'Content-Type': 'application/json' };
}

export function sendCustomersApiServiceAuthenticatedRequest(uri, method, data, onSuccessCallback, onFailureCallback) {
  asyncAuthRequestWithUrlResolver(uri, method, data, onSuccessCallback, onFailureCallback, null, null, undefined, undefined, customersApiServiceUrl, customersApiAuthHeaders);
}

function refreshToken(uri, method, data, onSuccessCallback, onFailureCallback, responseType, onFinallyCallback, signal, timeout, resolveUrl, buildHeaders) {
  const user = getLoggedInUser();
  const deviceId = getDeviceKey();

  axios({
    method: 'POST',
    url: url('/login/refresh-token'),
    data: JSON.stringify({
      email: user?.email,
      deviceId: deviceId
    }),
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true
  })
    .then(function (response) {
      const { currentTimeMillis, idTokenDurationSecs, csrfToken } = response.data;
      updateAccessTokenExpirationTime(currentTimeMillis, idTokenDurationSecs);
      if (csrfToken) {
        updateCsrfToken(csrfToken);
      }
      asyncAuthRequestWithUrlResolver(uri, method, data, onSuccessCallback, onFailureCallback, responseType, onFinallyCallback, signal, timeout, resolveUrl, buildHeaders);
    })
    .catch(function (error) {
      if (!error.response || error.response.status === 401) {
        logoutUser();
      } else {
        if (typeof onFailureCallback === 'function')
          onFailureCallback(error.response || error);
        else
          console.log(error.response);
      }
    });
}
